import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import ModalWrapper from '../Wrappers/ModalWrapper/ModalWrapper'
import AccordianWrapper from '../AccordionWrapper/AccordionWrapper'
import Typography from '../UI/Typography/Typography'
import useScreenSize from '../../hooks/useScreenSize'
import ImageLayout from '../ImageLayout/ImageLayout'
import lodash from "lodash"
import { displayTrendRadarChart } from './radar'
import { isNumber } from "../../helpers/types"
import { BREAKPOINTS } from '../../constants/breakpoints.constants'
import * as styles from "./TrendRadarChart-tw-styles"

const TrendRadarChart = props => {
  const { heading, description, config, content, variant } = props
  const style = styles[variant] || styles.defaultStyle
  const screen = useScreenSize()
  const wrapperEleRef = useRef()
  const [isModalOpen, setIsModalOpen] = useState(false)
  // in the useEffect, key press event handler is registered on first render so to get updated state using ref
  const isModelOpenRef = useRef()
  isModelOpenRef.current = isModalOpen
  const [isFullScreen, setIsFullScreen] = useState(false)
  // in the modal wrapper component, key press event handler is registered on first render so to get updated state using ref
  const isFullScreenRef = useRef()
  isFullScreenRef.current = isFullScreen
  const [section, setSection] = useState({ segmentIndex: null, trendIndex: null })
  const modalContainerEleId = `${variant}_MODAL_CONTAINER`


  const changeSection = useCallback((segmentIndex, trendIndex) => {
    setSection({ segmentIndex, trendIndex })
    document.body.classList.add("overflow-y-hidden")
    openModal()

    let duration = 500
    let id
    // useScreenSize will not work because of this function is passed on the first render only.
    if (window.screen.width >= BREAKPOINTS.lg) {
      id = modalContainerEleId
    } else {
      id = isNumber(trendIndex) ? content.contentCollection.items[segmentIndex].contentCollection.items.at(trendIndex).title : modalContainerEleId
    }
    if (isModalOpen && id === modalContainerEleId) {
      duration = 0
    }

    if (id === modalContainerEleId) {
      setTimeout(() => document.getElementById(id)?.scrollTo({ top: 0, behavior: 'smooth' }), duration);
    }
    else {
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: "start", behavior: 'smooth' }), duration);
    }
  }, [isModalOpen])


  const accordionGroup = useMemo(() => content && isNumber(section.segmentIndex) && {
    ...content.contentCollection.items[section.segmentIndex] || {},
    ...(content.contentCollection.items[section.segmentIndex] && {
      contentCollection: {
        items: content.contentCollection.items[section.segmentIndex].contentCollection?.items?.map((item, i) => {
          return {
            ...item,
            relatedItems: item.relatedItems?.array?.length > 0 && RelatedTrends({
              heading: item.relatedItems.heading, items: item.relatedItems.array.map((trend) => {
                return { segmentIndex: trend.segment - 1, trendIndex: trend.trend - 1 }
              })
            })
          }
        }) || []
      }
    }),
    ...(isNumber(section.trendIndex) && { activeIndex: section.trendIndex }),
    ...(content.contentCollection.items[section.segmentIndex].contentCollection?.items?.length > 0 && { includedItems: RelatedTrends({ heading: content.contentCollection.items[section.segmentIndex].includedItemsHeading, items: content.contentCollection.items[section.segmentIndex].contentCollection.items.map((_, i) => ({ segmentIndex: section.segmentIndex, trendIndex: i })) }) }),
    elementId: content.contentCollection?.items[section.segmentIndex]?.heading?.replaceAll(" ", "-")
  }, [section])

  const accordionProp = useMemo(() => !lodash.isEmpty(accordionGroup) && {
    contentCollection: {
      items:
        [
          accordionGroup
        ]
    }
  }, [section])

  const activeAccordionItem = useMemo(() => !lodash.isEmpty(accordionGroup) &&
    isNumber(section.trendIndex) &&
    accordionGroup.contentCollection.items.at(section.trendIndex)
    , [section])

  useEffect(() => {
    displayTrendRadarChart({ ...config, changeSection })
  }, [])

  useEffect(() => {
    // to handle escape key press
    document.addEventListener('keydown', handleCloseOnKeyPress)
    return () => document.removeEventListener('keydown', handleCloseOnKeyPress)
  }, [handleCloseOnKeyPress])

  function handleCloseOnKeyPress(event) {
    if (event.key === "Escape" || event.key === "Esc") {
      if (isModalOpen) {
        closeModal()
      } else if (isFullScreen) {
        closeFullScreen()
      }
    }
  }


  function openModal() {
    if (isFullScreenRef.current) {
      wrapperEleRef.current.classList.add("overflow-y-hidden")
    }
    setIsModalOpen(true)
  }

  function closeModal() {
    setIsModalOpen(false)
    if (!isFullScreenRef.current) {
      document.body.classList.remove("overflow-y-hidden")
    } else {
      wrapperEleRef.current.focus()
    }
  }

  function openFullScreen() {
    document.body.classList.add("overflow-y-hidden")
    setIsFullScreen(true)
  }

  function closeFullScreen() {
    if (!isModelOpenRef.current) {
      document.body.classList.remove("overflow-y-hidden")
    }
    wrapperEleRef.current.classList.remove("overflow-y-hidden")
    setIsFullScreen(false)
  }

  function toggleFullScreen() {
    if (isFullScreen) closeFullScreen()
    else openFullScreen()
  }


  function RelatedTrends({ heading, items }) {
    return (
      <div className={style.relatedTrendsWrapper}>
        {heading && <div className={style.relatedTrendsHeading}>{heading}</div>}
        <ul className={style.relatedTrends}>
          {items?.map((item, i) => {
            const trendName = content.contentCollection.items[item.segmentIndex].contentCollection.items[item.trendIndex].title
            return (
              <li key={trendName} className={style.relatedTrend} style={{ color: config.categories[item.segmentIndex].textColor }} onClick={() => changeSection(item.segmentIndex, item.trendIndex)}>
                <span className={style.relatedTrendBullet} style={
                  {
                    borderColor: config.categories[item.segmentIndex].textColor,
                    backgroundColor: config.categories[item.segmentIndex].bgColor
                  }
                }></span>
                <span className={style.relatedTrendLabel}>{trendName}</span>
              </li>
            )
          }
          )}
        </ul>
      </div>
    )
  }
  RelatedTrends.propTypes = {
    heading: PropTypes.string,
    items: PropTypes.object,
    activeIndex: PropTypes.number,
    image: PropTypes.object
  }

  function onSwipeRight() {
    closeModal()
  }

  return (
    <>
      <div ref={wrapperEleRef} className={`${style.radarWrapper} ${isFullScreen ? style.fullScreenMode : ""}`} tabIndex={"-1"} data-testid="TrendRdarChart-wrapper">
        <div className={style.container}>
          {heading && <div className={style.heading} data-testid="TrendRadarChart-heading">{heading}</div>}
          {description && <div className={style.description}><Typography content={description} data-testid="TrendRadarChart-description" /></div>}
          <div className={style.chartContainer}>
            <div className={style.fullScreenBtn} onClick={toggleFullScreen}>{isFullScreen ? "Exit Full Screen" : "Full Screen"}</div>
            <div className={style.legendWrapper}>
              {config.legendHeading && <p className={style.legendHeading}>{config.legendHeading}</p>}
              {config.entryWeight && <ul className={style.lengends}>
                {Object.entries(config.entryWeight).map((legend, i) => (<li key={i} className={style.legend}><span className={`${style.legendNotation} ${legend[0]}`} style={{
                  width: legend[1].radius * 2,
                  height: legend[1].radius * 2
                }}></span><span className={style.legendName}>{legend[1].label}</span></li>))}
              </ul>}
            </div>
            {(config.desktopScreenhint && config.mobileScreenhint) && <div className={style.hint}>{screen?.isLarge ? config.desktopScreenhint : config.mobileScreenhint}</div>}
            <svg id={config.svg_id} className={style.radar}></svg>
          </div>
        </div>
      </div>
      {
        <ModalWrapper open={isModalOpen} containerEleId={modalContainerEleId} closeModal={closeModal} closeOnEscKeyPress={false}
          closeOnBodyclick
          {...(screen && !screen.isLarge) && { onSwipeRight }}
          variant={variant} closeBtnLabel={"X"} >
          {accordionProp && screen && !screen.isLarge &&
            <AccordianWrapper {...accordionProp} variant={`${variant}_MOBILE_SCREEN_ACCORDION`} callBack={changeSection} />
          }
          {activeAccordionItem && screen?.isLarge &&
            <div className={style.desktopModalWrapper}>
              <ImageLayout
                title={accordionGroup.heading}
                onClickOnTitle={() => changeSection(section.segmentIndex)}
                subtitle={activeAccordionItem.title}
                description={activeAccordionItem.description}
                image={activeAccordionItem.mainImage}
                variant={`${variant}_TREND_MODAL`}
                elementId={accordionGroup.elementId}
                endNotes={activeAccordionItem.endNotes}
              >
                {activeAccordionItem.shortDescription &&
                  <Typography
                    content={activeAccordionItem.shortDescription}
                    className={style.desktopModalShortDescription}
                    allow_br_tag={true}
                  />}
                {activeAccordionItem.relatedItems}
              </ImageLayout>
            </div>
          }
          {(accordionGroup && !activeAccordionItem) && screen?.isLarge &&
            <div className={style.desktopModalWrapper}>
              <ImageLayout
                subtitle={accordionGroup.heading}
                imagesCollection={{
                  items: accordionGroup.image ? [accordionGroup.image] : []
                }
                }
                description={accordionGroup.description}
                variant={`${variant}_SEGMENT_MODAL`}
                elementId={accordionGroup.elementId}
                endNotes={accordionGroup.endNotes}
              >
                {accordionGroup.includedItems}
              </ImageLayout>
            </div>
          }
        </ModalWrapper>
      }
    </>
  )
}

TrendRadarChart.propTypes = {
  heading: PropTypes.string,
  description: PropTypes.string,
  config: PropTypes.object,
  content: PropTypes.object,
  variant: PropTypes.string
}

export default memo(TrendRadarChart)